import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Terminal, Lock, Cloud, Shield } from 'lucide-react';

const LoadingScreen = ({ onLoadComplete }) => {
  const [bootText, setBootText] = useState([]);
  const [progress, setProgress] = useState(0);

  const bootSequence = [
    '> Initializing DevSecOps Environment...',
    '> Loading security protocols...',
    '> Connecting to cloud infrastructure...',
    '> Establishing secure connection...',
    '> Mounting encrypted volumes...',
    '> Starting services...',
    '> System ready.',
    '> Welcome to Ankit Ojha Portfolio',
  ];

  useEffect(() => {
    let currentLine = 0;
    let progressInterval;

    const bootInterval = setInterval(() => {
      if (currentLine < bootSequence.length) {
        setBootText((prev) => [...prev, bootSequence[currentLine]]);
        currentLine++;
      } else {
        clearInterval(bootInterval);
        setTimeout(() => onLoadComplete(), 500);
      }
    }, 300);

    progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 2;
      });
    }, 50);

    return () => {
      clearInterval(bootInterval);
      clearInterval(progressInterval);
    };
  }, [onLoadComplete]);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'var(--bg-primary)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
      }}
    >
      {/* Animated Icons */}
      <div
        style={{
          display: 'flex',
          gap: '30px',
          marginBottom: '40px',
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        >
          <Shield size={40} color="var(--brand-primary)" />
        </motion.div>
        <motion.div
          animate={{ y: [-10, 10, -10] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Cloud size={40} color="var(--brand-primary)" />
        </motion.div>
        <motion.div
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Lock size={40} color="var(--brand-primary)" />
        </motion.div>
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
        >
          <Terminal size={40} color="var(--brand-primary)" />
        </motion.div>
      </div>

      {/* Boot Terminal */}
      <div
        className="glass"
        style={{
          width: '100%',
          maxWidth: '700px',
          padding: '30px',
          borderRadius: '0px',
          border: '1px solid var(--border-medium)',
        }}
      >
        <div
          style={{
            fontFamily: "'Courier New', monospace",
            fontSize: '14px',
            lineHeight: '1.8',
            color: 'var(--brand-primary)',
            minHeight: '200px',
          }}
        >
          {bootText.map((line, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              style={{ marginBottom: '8px' }}
            >
              {line}
            </motion.div>
          ))}
          <span className="terminal-cursor" />
        </div>

        {/* Progress Bar */}
        <div
          style={{
            marginTop: '30px',
            width: '100%',
            height: '4px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '0px',
            overflow: 'hidden',
          }}
        >
          <motion.div
            initial={{ width: '0%' }}
            animate={{ width: `${progress}%` }}
            style={{
              height: '100%',
              background: 'var(--brand-primary)',
              boxShadow: '0 0 10px var(--brand-glow)',
            }}
          />
        </div>
        <div
          style={{
            marginTop: '10px',
            textAlign: 'right',
            fontSize: '12px',
            color: 'var(--text-muted)',
            fontFamily: "'Courier New', monospace",
          }}
        >
          {progress}%
        </div>
      </div>
    </motion.div>
  );
};

export default LoadingScreen;
